<!--
  La barre latérale est celle de LP-FIN-APP (src/app/layout/shell/) et de
  latouretpetit (src/app/layout/layout.page.html) — le standard L&P, repris à
  l'identique le 2026-10-13 : logo centré sur son filet et le nom de l'application
  dessous, sections en capitales, entrées Font Awesome Pro Light à largeur fixe,
  entrée active en primary-50 / primary-800, profil et « Se déconnecter » dans
  le pied. Ce qui n'existe que dans GELO reste : la date de travail, le lanceur
  des palettes, la notification d'intégration.

  Le menu se replie à partir de `md` (le bouton « Masquer le menu »), comme
  dans LP-FIN-APP : les tableaux de l'éclatement et des baux valent la pleine
  largeur. Le choix est retenu dans le navigateur. En dessous de `md` il
  s'ouvre en tiroir depuis la barre du haut.
-->
<a
  href="#main-content"
  class="sr-only rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-900 focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:outline-2 focus:outline-primary-800"
>
  Aller au contenu
</a>

<div class="flex h-screen overflow-hidden bg-app-bg">
  @if (menuOpen()) {
    <div
      class="fixed inset-0 z-30 bg-gray-900/40 md:hidden"
      aria-hidden="true"
      (click)="closeMenu()"
    ></div>
  }

  <aside
    id="app-sidebar"
    class="fixed inset-y-0 left-0 z-40 w-64 shrink-0 flex-col border-r border-gray-200 bg-white md:relative md:z-auto"
    [class.flex]="menuOpen()"
    [class.hidden]="!menuOpen()"
    [class.md:flex]="!collapsed()"
    aria-label="Barre latérale"
  >
    <!-- Un seul emplacement, deux boutons : fermer le tiroir sous `md`, replier le menu au-dessus. -->
    <button
      type="button"
      class="absolute top-2 right-2 rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800 md:hidden"
      aria-controls="app-sidebar"
      aria-expanded="true"
      title="Fermer le menu"
      (click)="closeMenu()"
    >
      <span class="sr-only">Fermer le menu</span>
      <fa-icon [icon]="faXmark" class="text-[17px] leading-none" aria-hidden="true"></fa-icon>
    </button>
    <button
      #collapseButton
      type="button"
      class="absolute top-2 right-2 hidden rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800 md:inline-flex"
      aria-controls="app-sidebar"
      aria-expanded="true"
      title="Masquer le menu"
      (click)="collapse()"
    >
      <span class="sr-only">Masquer le menu</span>
      <fa-icon [icon]="faAnglesLeft" class="text-[17px] leading-none" aria-hidden="true"></fa-icon>
    </button>

    <div class="mx-5 flex shrink-0 flex-col items-center border-b border-gray-200 pt-6 pb-4">
      <img
        ngSrc="assets/img/Logo_sidemenu.png"
        width="728"
        height="421"
        priority
        alt="Latour &amp; Petit"
        class="h-auto w-full max-w-[160px]"
      />
      <div class="mt-4 h-px w-10 bg-gray-300" aria-hidden="true"></div>
      <p class="mt-3 text-sm font-semibold tracking-wide text-gray-700">L&amp;P GELO</p>
    </div>

    <!-- La zone qui défile : date de travail, menu, lanceur des palettes. -->
    <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <!--
        Date de travail, sur le modèle de Business Central : encoder le
        rapprochement d'avril en mai ne doit pas obliger à retaper la date sur
        chaque ligne. Réservée aux écrans comptables — ce sont les seuls qui
        datent des écritures ; `canSeeWorkingDate` porte la condition.
      -->
      @if (canSeeWorkingDate) {
        <div class="shrink-0 px-5 pt-4">
          <div class="flex items-center justify-between gap-2 pb-1.5">
            <span class="text-xs font-semibold tracking-wide text-gray-600 uppercase">Date de travail</span>
            @if (workingDateIsSet) {
              <button
                type="button"
                (click)="clearWorkingDate()"
                class="cursor-pointer text-xs font-medium text-primary-800 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800"
                title="Revenir au jour courant"
              >
                Aujourd'hui
              </button>
            }
          </div>
          <!-- Couleur de marque dès qu'une date est figée : c'est ce qui évite
               qu'un avril oublié continue de dater les écritures en juin. -->
          <input
            type="date"
            [ngModel]="workingDateValue"
            (ngModelChange)="onWorkingDateChange($event)"
            aria-label="Date de travail"
            title="Date proposée par défaut dans les écritures, par exemple la date d'imputation des lignes de répartition. Le jour courant la retire."
            class="w-full cursor-pointer rounded-md border px-3 py-2 text-sm tabular-nums focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800"
            [class.border-gray-300]="!workingDateIsSet"
            [class.bg-white]="!workingDateIsSet"
            [class.text-gray-700]="!workingDateIsSet"
            [class.border-primary-800]="workingDateIsSet"
            [class.bg-primary-50]="workingDateIsSet"
            [class.text-primary-800]="workingDateIsSet"
            [class.font-medium]="workingDateIsSet"
          />
        </div>
      }

      <nav class="flex-1 px-3 py-2" aria-label="Navigation principale">
        <!-- Le tableau de bord, hors groupe : ouvert à quiconque a un espace. -->
        <ul class="space-y-1">
          @if (hasAnyCapability) {
            <li><ng-container *ngTemplateOutlet="navLink; context: { $implicit: home }"></ng-container></li>
          }
        </ul>

        <!-- Les groupes du menu : un groupe n'apparaît que si l'utilisateur
             porte une de ses capacités. -->
        @for (group of activeNavGroups; track group.id) {
          <h2 [id]="'nav-section-' + group.id" class="nav-section">{{ group.label }}</h2>
          <ul class="space-y-1" [attr.aria-labelledby]="'nav-section-' + group.id">
            @for (item of group.items; track item.route) {
              <li><ng-container *ngTemplateOutlet="navLink; context: { $implicit: item }"></ng-container></li>
            }
          </ul>
        }

        <ul class="mt-4 space-y-1">
          <li>
            <button
              type="button"
              class="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800"
              aria-haspopup="dialog"
              (click)="openModal()"
            >
              <fa-icon [icon]="faLifeRing" [fixedWidth]="true" class="text-[17px] leading-none" aria-hidden="true"></fa-icon>
              Support
            </button>
          </li>
        </ul>
      </nav>

      <!-- ===== Lanceur des palettes =====
           Un onglet collé au bas de la partie du menu qui défile (sticky), et non
           une bande : une bande opaque coupait le pied du menu. Placé DANS la
           zone qui défile, il a exactement la largeur et le centrage des entrées
           du menu, barre de défilement comprise — il recouvre l'entrée qui passe
           derrière lui. Choix du 2026-09-25. -->
      @if (canUsePalettes) {
        <div class="sticky bottom-0 mt-4 shrink-0 px-3">
          <app-palette-switcher class="block w-full" [compact]="true" (switchTo)="openPalette($event)" />
        </div>
      }
    </div>

    <div class="shrink-0 border-t border-gray-200 p-4">
      <div class="flex items-center gap-3">
        @if (user; as u) {
          <span
            class="flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary-600 text-sm font-semibold text-white"
            aria-hidden="true"
          >
            {{ initials }}
          </span>
          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-gray-900">{{ u.firstName }} {{ u.lastName }}</p>
            <p class="truncate text-xs text-gray-700">{{ u.email }}</p>
          </div>
        } @else {
          <div class="size-9 shrink-0 animate-pulse rounded-full bg-gray-200" aria-hidden="true"></div>
          <div class="flex min-w-0 flex-1 flex-col gap-1" role="status">
            <span class="sr-only">Chargement du profil</span>
            <div class="h-4 w-4/5 animate-pulse rounded-md bg-gray-200" aria-hidden="true"></div>
            <div class="h-3 w-3/5 animate-pulse rounded-md bg-gray-200" aria-hidden="true"></div>
          </div>
        }
      </div>
      <button
        type="button"
        class="mt-4 w-full rounded-md px-3 py-2 text-sm font-medium text-gray-900 ring-1 ring-gray-300 hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800 disabled:cursor-wait disabled:opacity-75"
        [disabled]="signingOut()"
        (click)="onLogout()"
      >
        {{ signingOut() ? 'Déconnexion…' : 'Se déconnecter' }}
      </button>
    </div>
  </aside>

  <!-- ===== Main Content ===== -->
  <div class="flex min-w-0 flex-1 flex-col overflow-hidden">
    <!-- Barre du haut : sous `md`, et au-dessus tant que le menu est replié. -->
    <header
      class="flex h-14 shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4"
      [class.md:hidden]="!collapsed()"
    >
      @if (collapsed()) {
        <button
          #expandButton
          type="button"
          class="hidden rounded-md p-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800 md:inline-flex"
          aria-controls="app-sidebar"
          aria-expanded="false"
          title="Afficher le menu"
          (click)="expand()"
        >
          <span class="sr-only">Afficher le menu</span>
          <fa-icon [icon]="faAnglesRight" class="text-[20px] leading-none" aria-hidden="true"></fa-icon>
        </button>
      }
      <img
        ngSrc="assets/img/Logo_sidemenu.png"
        width="728"
        height="421"
        priority
        alt="Latour &amp; Petit"
        class="h-10 w-auto"
      />
      <button
        type="button"
        class="ml-auto rounded-md p-2 text-gray-900 hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800 md:hidden"
        aria-controls="app-sidebar"
        [attr.aria-expanded]="menuOpen()"
        (click)="toggleMenu()"
      >
        <span class="sr-only">{{ menuOpen() ? 'Fermer le menu' : 'Ouvrir le menu' }}</span>
        <fa-icon [icon]="menuOpen() ? faXmark : faBars" class="text-[20px] leading-none" aria-hidden="true"></fa-icon>
      </button>
    </header>

    <main id="main-content" tabindex="-1" class="flex-1 overflow-y-auto bg-app-bg focus:outline-none">
      <router-outlet></router-outlet>
    </main>
  </div>

  <!-- ===== Integration Notification ===== -->
  @if (showIntegrationNotification) {
    <div class="fixed bottom-4 right-4 z-50 bg-white border border-yellow-200 rounded-2xl shadow-2xl max-w-sm p-4 transform transition-all duration-300 hover:scale-105">
      <div class="flex items-start space-x-3">
        <div class="w-8 h-8 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
          </svg>
        </div>
        <div class="flex-1">
          <p class="text-sm font-semibold text-gray-900">
            {{ pendingIntegrationCount }} fichier{{ pendingIntegrationCount > 1 ? 's' : '' }} en attente
          </p>
          <p class="text-xs text-gray-600 mt-1">
            Consultez la section <span class="font-medium text-primary-800">Transactions</span> pour les traiter.
          </p>
        </div>
      </div>
    </div>
  }
</div>

<!-- Une entrée du menu. `paths: 'subset'` : la fiche d'un bail garde « Baux » actif. -->
<ng-template #navLink let-item>
  <a
    class="nav-link flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-800"
    [routerLink]="item.route"
    routerLinkActive="nav-link-active"
    [routerLinkActiveOptions]="{ paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }"
    ariaCurrentWhenActive="page"
    (click)="closeMenu()"
  >
    <!-- `fixedWidth` : toutes les icônes occupent la même largeur, donc les
         libellés s'alignent quelle que soit la forme du glyphe. -->
    <fa-icon [icon]="item.icon" [fixedWidth]="true" class="text-[17px] leading-none" aria-hidden="true"></fa-icon>
    {{ item.label }}
  </a>
</ng-template>

<!-- ===== Support Modal ===== -->
@if (modalOpen) {
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4"
    role="presentation"
    tabindex="0"
    (keydown)="onKeydown($event)"
    aria-hidden="false"
  >
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" (click)="closeModal()" aria-hidden="true"></div>

    <!-- Dialog -->
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-modal-title"
      class="relative w-full max-w-xl mx-auto bg-white rounded-lg shadow-xl ring-1 ring-black/5 overflow-hidden"
    >
      <div class="flex items-start justify-between p-4 border-b border-gray-100">
        <h2 id="support-modal-title" class="text-lg font-semibold text-gray-900">
          Contacter le support
        </h2>
        <button
          type="button"
          (click)="closeModal()"
          class="ml-3 inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-800"
          aria-label="Fermer la fenêtre de support"
        >
          <fa-icon [icon]="faXmark" class="text-[20px] leading-none" aria-hidden="true"></fa-icon>
        </button>
      </div>

      <div class="p-4 space-y-4 text-sm text-gray-700">
        <h3 class="font-medium text-sm text-gray-900">Comment contacter le support ?</h3>

        <div class="space-y-2">
          <div class="flex items-center gap-3">
            <span class="font-medium w-28 text-gray-600">Par Email:</span>
            <a class="text-primary-800 hover:underline" href="mailto:helpdesk@avsit.io">helpdesk&#64;avsit.io</a>
          </div>
          <div class="flex items-center gap-3">
            <span class="font-medium w-28 text-gray-600">Par téléphone:</span>
            <a class="text-primary-800 hover:underline" href="tel:+3225855336">+32 2 585 53 36</a>
          </div>
        </div>

        <div class="pt-2 border-t border-gray-100">
          <p class="text-xs text-gray-500">
            Vous pouvez contacter le support pour toute demande de fonctionnalité ou rapport de bug.
          </p>
          <p class="text-xs text-gray-500 mt-2">
            Si vous signalez un problème, veuillez décrire la manière de le reproduire et si possible inclure des captures d'écran illustrant le problème.
          </p>
        </div>
      </div>

      <div class="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-100">
        <span class="text-xs text-gray-600">Version {{ version }}</span>
        <button
          type="button"
          (click)="closeModal()"
          class="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-800"
        >
          Fermer
        </button>
      </div>
    </div>
  </div>
}
